import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setActivePage } from "../store/activePage";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import createCustomer from "../hooks/createCustomer";
import { SuccessToast } from "../components/toastify";
import CitySearchSelect from "../components/city-search-select";

// ─── Field Box ────────────────────────────────────────────────────────────────
function FieldBox({ label, required, error, children, span }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs font-semibold text-red-400 mt-1">{error}</p>
      )}
    </div>
  );
}

// ─── Input class helper ───────────────────────────────────────────────────────
const tamilInputCls =
  "force-tamil w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20";

const plainInputCls =
  "no-translate w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20";

// ─── Section Header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, sub }) {
  return (
    <div className="mb-4 flex items-center gap-3 border-b border-slate-200 pb-3">
      <span className="text-xl">{icon}</span>
      <div>
        <p className="text-sm font-black text-slate-900">{title}</p>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function OperatorAddCustomer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const shouldReturnToForm = queryParams.get("fromEventCreation") === "true";
  const prefilledMobile = queryParams.get("mobile") || "";
  const returnTo = queryParams.get("returnTo") || "create-new-event";
  const returnPath =
    returnTo === "old-entries-upload" || returnTo === "old-record-upload"
      ? "/dashboard/old-record-upload"
      : "/dashboard/create-new-event";

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required("First name is required"),
    last_name: Yup.string()
      .required("Last name is required"),
    occupation: Yup.string()
      .required("Occupation is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Must be 10 digits")
      .required("Mobile is required"),
    email: Yup.string().email("Invalid email"),
    plot_no: Yup.string().required("Plot number is required"),
    address: Yup.string()
      .required("Address is required"),
    city: Yup.string()
      .required("City is required"),
    state: Yup.string()
      .required("State is required"),
    pincode: Yup.string()
      .matches(/^[0-9]{6}$/, "Must be 6 digits")
      .required("Pincode is required"),
  });

  const initialValues = {
    first_name: "",
    last_name: "",
    occupation: "",
    mobile: prefilledMobile,
    email: "",
    plot_no: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    values["password"] = values["mobile"];
    const regRes = await createCustomer(values);
    if (regRes) {
      SuccessToast(
        "Customer created. Mobile number will be their password."
      );
      if (shouldReturnToForm) {
        const nextParams = new URLSearchParams({
          customerMobile: values.mobile,
        });
        navigate(`${returnPath}?${nextParams.toString()}`);
      } else {
        resetForm();
      }
    }
  };

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Add Customer",
        activeMenu: "addCustomer",
      })
    );
  }, []);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_42%,#edf4f8_100%)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-3xl space-y-5">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 rounded-3xl bg-[linear-gradient(135deg,#f8fbfd_0%,#edf7f6_50%,#d8f0ec_100%)] px-6 py-5 shadow-lg shadow-slate-200/70 ring-1 ring-white/70">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-violet-200 bg-violet-50 text-2xl">
            👤
          </div>
          <div>
            <h1 className="text-lg font-black tracking-wide text-slate-900">
              Add Customer
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Register a new customer to your account
            </p>
          </div>
        </div>

        {/* ── Form Card ──────────────────────────────────────────────────── */}
        <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/95 shadow-xl shadow-slate-200/70">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, values, setFieldValue, setFieldTouched }) => (
              <Form>
                <div className="p-6 space-y-6">

                  {/* ── Personal Info ──────────────────────────────────── */}
                  <div>
                    <SectionHeader
                      icon="🪪"
                      title="Personal Information"
                      sub="Name and contact details"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FieldBox
                        label="First Name"
                        required
                        error={touched.first_name && errors.first_name}
                      >
                          <Field
                          name="first_name"
                          type="text"
                          placeholder="Enter first name"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Last Name"
                        required
                        error={touched.last_name && errors.last_name}
                      >
                          <Field
                          name="last_name"
                          type="text"
                          placeholder="Enter last name"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Occupation"
                        required
                        error={touched.occupation && errors.occupation}
                      >
                          <Field
                          name="occupation"
                          type="text"
                          placeholder="Enter occupation"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Mobile Number"
                        required
                        error={touched.mobile && errors.mobile}
                      >
                          <Field
                          name="mobile"
                          type="text"
                          placeholder="10-digit number"
                          maxLength={10}
                          className={plainInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Email (Optional)"
                        error={touched.email && errors.email}
                      >
                          <Field
                          name="email"
                          type="email"
                          placeholder="Optional"
                          className={plainInputCls}
                        />
                      </FieldBox>
                    </div>
                  </div>

                  {/* ── Address Info ───────────────────────────────────── */}
                  <div>
                    <SectionHeader
                      icon="📍"
                      title="Address Details"
                      sub="Plot, city, state and pincode"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FieldBox
                        label="Plot No"
                        required
                        error={touched.plot_no && errors.plot_no}
                      >
                          <Field
                          name="plot_no"
                          type="text"
                          placeholder="e.g. 12A"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Address"
                        required
                        span={2}
                        error={touched.address && errors.address}
                      >
                          <Field
                          name="address"
                          type="text"
                          placeholder="Street / Area"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="City"
                        required
                        error={touched.city && errors.city}
                      >
                        <CitySearchSelect
                          value={values.city}
                          onChange={(city) => setFieldValue("city", city)}
                          onBlur={() => setFieldTouched("city", true)}
                          className={tamilInputCls}
                          placeholder="Search Tamil Nadu city…"
                        />
                      </FieldBox>

                      <FieldBox
                        label="State"
                        required
                        error={touched.state && errors.state}
                      >
                          <Field
                          name="state"
                          type="text"
                          placeholder="State"
                          className={tamilInputCls}
                        />
                      </FieldBox>

                      <FieldBox
                        label="Pincode"
                        required
                        error={touched.pincode && errors.pincode}
                      >
                          <Field
                          name="pincode"
                          type="text"
                          placeholder="6-digit pincode"
                          maxLength={6}
                          className={plainInputCls}
                        />
                      </FieldBox>
                    </div>
                  </div>
                </div>

                {/* ── Info note ─────────────────────────────────────── */}
                <div className="mx-6 mb-4 flex items-start gap-2.5 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
                  <span className="text-amber-400 mt-0.5 shrink-0">ℹ️</span>
                  <p className="text-xs text-slate-600">
                    The customer's{" "}
                    <span className="font-semibold text-slate-900">
                      mobile number
                    </span>{" "}
                    will be set as their default login password.
                  </p>
                </div>

                {/* ── Submit ────────────────────────────────────────── */}
                <div className="px-6 pb-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group flex w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] py-3.5 text-sm font-bold text-white shadow-lg shadow-teal-200/70 transition duration-200 hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-teal-500/30 disabled:opacity-50"
                  >
                    <span>Submit & Create Customer</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}

export default OperatorAddCustomer;
